import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getEvent, joinEvent } from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Calendar, MapPin, Tag, Users,
  ShieldCheck, EyeOff, Settings, FileText, Share2, Check, Clock,
} from "lucide-react";
import { toast } from "sonner";
import ShareEventModal from "@/components/SharePromptModal";
import NearbyPanel from "@/components/NearbyPanel";
import EventMap from "@/components/EventMap";
import Avatar from "@/components/Avatar";
import { coverFor } from "@/lib/defaultEventImages";
import ReportButton from "@/components/ReportButton";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

// Date-based "ended" check matching the discovery filter in lib/events.js.
// An event has ended once the viewer's local calendar day is past the
// event's local calendar day.
function hasEventEnded(iso) {
  try {
    const dt = new Date(iso);
    const evDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return evDay < today;
  } catch { return false; }
}

export default function EventDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [joining, setJoining] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const data = await getEvent(id, user?.id);
      if (!data) setNotFound(true);
      else setEvent(data);
    } catch { setNotFound(true); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await getEvent(id, user?.id).catch(() => null);
      if (!alive) return;
      if (!data) setNotFound(true);
      else setEvent(data);
    })();
    return () => { alive = false; };
  }, [id, user?.id]);

  const onJoin = async () => {
    if (!user) { navigate(`/login?next=/events/${id}`); return; }
    setJoining(true);
    try {
      const status = await joinEvent(id, user.id, !!event.requires_approval);
      await load();
      toast.success(status === "pending" ? "Request sent. The host will decide." : "You're in. See you there.");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setJoining(false); }
  };

  if (notFound) {
    return (
      <div className="bg-paper min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-display text-4xl text-ink">Event not found.</p>
          <Link to="/feed" className="inline-block mt-6 cta-ghost">← Back to feed</Link>
        </div>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="bg-paper min-h-[60vh] px-5 md:px-10 pt-10 max-w-3xl mx-auto">
        <div className="h-10 w-32 bg-ink/10 rounded-full animate-pulse mb-8" />
        <div className="pop p-0 overflow-hidden">
          <div className="aspect-[16/10] bg-ink/10 animate-pulse" />
          <div className="p-6 space-y-4">
            <div className="h-9 w-3/4 bg-ink/10 rounded-md animate-pulse" />
            <div className="h-4 w-1/2 bg-ink/10 rounded-md animate-pulse" />
            <div className="h-20 w-full bg-ink/10 rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const isHost = user?.id === event.host_id;
  const canSeeExact = isHost || event.joined;
  const atCapacity = event.capacity != null && event.attendees_count >= event.capacity;
  const ended = hasEventEnded(event.date_time);

  let buttonLabel = "Join the thing";
  let buttonIcon = null;
  if (ended) { buttonLabel = "Event ended"; buttonIcon = <Clock size={16} strokeWidth={2.5} />; }
  else if (event.joined) { buttonLabel = "You're in"; buttonIcon = <Check size={16} strokeWidth={3} />; }
  else if (event.pending) { buttonLabel = "Request pending"; buttonIcon = <Clock size={16} strokeWidth={2.5} />; }
  else if (atCapacity) buttonLabel = "At capacity";
  else if (event.requires_approval) buttonLabel = "Request to join";
  else if (joining) buttonLabel = "Joining…";

  const buttonDisabled = ended || event.joined || event.pending || joining || atCapacity;

  const ctaClasses = ended
    ? "cta-ghost !text-ink/55"
    : event.joined
    ? "cta-ghost bg-lime !text-ink"
    : event.pending
    ? "cta-ghost !text-ink/55"
    : atCapacity
    ? "cta-ghost !text-ink/55"
    : "cta-primary";

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-5 md:px-10 pt-6 md:pt-10 pb-32 md:pb-12">
        <div className="flex items-center justify-between mb-6 md:mb-8 gap-4">
          <Link
            to="/feed"
            data-testid="event-back-link"
            className="inline-flex items-center justify-center w-11 h-11 border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors"
            aria-label="Back to feed"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
          </Link>
          <button
            data-testid="event-share-button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
          >
            <Share2 size={14} strokeWidth={2.5} /> Share
          </button>
        </div>

        {/* HERO CARD */}
        <div className="pop p-0 overflow-hidden">
          <div className="relative aspect-[16/10] bg-[#EDE7D2] border-b-[3px] border-ink">
            <img src={coverFor(event)} alt={event.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <span className="absolute top-4 left-4 inline-flex items-center bg-paper border-[2.5px] border-ink rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest shadow-pop-sm">
              {event.category}
            </span>
            <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
              {event.requires_approval && (
                <span className="inline-flex items-center gap-1.5 bg-paper border-[2.5px] border-ink rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest shadow-pop-sm">
                  <ShieldCheck size={12} strokeWidth={2.5} /> Approval
                </span>
              )}
              {event.is_private && (
                <span className="inline-flex items-center gap-1.5 bg-paper border-[2.5px] border-ink rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest shadow-pop-sm">
                  <EyeOff size={12} strokeWidth={2.5} /> Private
                </span>
              )}
            </div>
          </div>

          <div className="p-6 md:p-8 flex flex-col gap-7">
            {ended && (
              <div
                data-testid="event-ended-banner"
                className="inline-flex items-center gap-2 self-start bg-ink text-paper border-[2.5px] border-ink rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.25em]"
              >
                <Clock size={12} strokeWidth={3} /> This event has ended
              </div>
            )}
            <h1 data-testid="event-title" className="font-display text-3xl md:text-5xl tracking-tight leading-[0.98] text-ink">
              {event.title}
            </h1>

            <div className="grid sm:grid-cols-2 gap-3">
              <Stat icon={Calendar} label="When">{formatDate(event.date_time)}</Stat>
              <Stat icon={MapPin}   label="Where">{event.city}</Stat>
              <Stat icon={Tag}      label="Category">{event.category}</Stat>
              <Stat icon={Users}    label="Going">
                {event.attendees_count}{event.capacity ? ` / ${event.capacity}` : ""}
                {event.capacity ? "" : event.attendees_count === 1 ? " person" : " people"}
              </Stat>
            </div>

            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-ink/60 mb-2">The vibe</p>
              <p data-testid="event-description" className="text-ink/80 leading-relaxed whitespace-pre-line text-[1.0625rem]">
                {event.description}
              </p>
            </div>

            {event.additional_notes && (
              <div className="border-[2.5px] border-ink rounded-2xl p-4 flex gap-3 bg-cream">
                <FileText size={18} strokeWidth={2} className="text-ink shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest text-ink/60 mb-1">From the host</p>
                  <p className="text-ink/80 whitespace-pre-line text-sm leading-relaxed">{event.additional_notes}</p>
                </div>
              </div>
            )}

            {event.latitude != null && event.longitude != null && (
              <div data-testid="event-map-section" className="flex flex-col gap-2">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-ink/60">
                  {canSeeExact ? "Exact location" : "Rough area"}
                </p>
                <EventMap event={event} canSeeExact={canSeeExact} />
              </div>
            )}

            <div className="flex items-center gap-3 border-t-2 border-dashed border-ink/15 pt-5">
              <Avatar src={event.host_avatar_url} name={event.host_name} size="md" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-ink/55">Hosted by</span>
                {event.host_id ? (
                  <Link to={`/u/${event.host_username || event.host_id}`} data-testid="event-host-link"
                    className="text-ink hover:underline text-base font-bold truncate">
                    {event.host_username ? `@${event.host_username}` : (event.host_name || "anon")}
                  </Link>
                ) : (
                  <span className="text-ink text-base font-bold">{event.host_name || "anon"}</span>
                )}
              </div>
              {!isHost && (
                <ReportButton targetType="event" targetId={event.id} testid="event-report-btn" />
              )}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:block">
              {isHost ? (
                <Link to={`/events/${id}/manage`} data-testid="event-manage-button" className="cta-ghost w-full">
                  <Settings size={16} strokeWidth={2.5} /> Manage event
                </Link>
              ) : (
                <button
                  data-testid="join-event-button"
                  disabled={buttonDisabled}
                  onClick={onJoin}
                  className={`${ctaClasses} w-full`}
                >
                  {buttonIcon} {buttonLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom RSVP on mobile */}
      <div
        className="md:hidden fixed left-0 right-0 z-40 bg-paper/95 backdrop-blur-md border-t-[3px] border-ink px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem+5rem)]"
        style={{ bottom: 0 }}
      >
        {isHost ? (
          <Link to={`/events/${id}/manage`} data-testid="event-manage-button-mobile" className="cta-ghost w-full">
            <Settings size={16} strokeWidth={2.5} /> Manage event
          </Link>
        ) : (
          <button
            data-testid="join-event-button-mobile"
            disabled={buttonDisabled}
            onClick={onJoin}
            className={`${ctaClasses} w-full`}
          >
            {buttonIcon} {buttonLabel}
          </button>
        )}
      </div>

      {(event.joined || isHost) && !ended && (
        <NearbyPanel event={event} />
      )}

      {shareOpen && (
        <ShareEventModal event={event} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, children }) {
  return (
    <div className="border-[2.5px] border-ink rounded-2xl px-4 py-3 bg-cream">
      <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-ink/55">
        <Icon size={11} strokeWidth={2.5} /> {label}
      </span>
      <p className="text-ink font-bold leading-snug text-sm mt-1">{children}</p>
    </div>
  );
}
